/**
 * Fetch-based API client for the Grain Breakeven Tracker backend.
 *
 * Wraps `fetch` to the backend `/api/*` routes (design: API Endpoints). It
 * stores the session token from `POST /api/auth/login` and attaches it as an
 * `Authorization: Bearer <token>` header on every subsequent request — the
 * header the Lambda authorizer reads (it also accepts a `session` cookie or
 * `x-session-id`, but bearer is the canonical path). On any `401` from a
 * protected route it clears the stored session and invokes the injected
 * `onUnauthorized` callback so the app routes back to the Passcode entry
 * interface (Req 1.6).
 *
 * Each method returns a structured {@link ApiResult} rather than throwing on
 * non-2xx, so views can surface validation errors (400), not-found (404), and
 * lockout (423) inline without try/catch noise. Genuine network failures
 * resolve to `{ ok: false, status: 0 }` so the price surface can stay
 * interactive while retrieval is pending/unavailable (Req 9.7).
 */
import { apiBaseUrl, type Crop, type TargetType } from "../config";
import { clearSession, loadSession, saveSession } from "../session";
import type {
  BreakevenResponse,
  CropInputsResponse,
  DashboardResponse,
  LoginSuccess,
  OutcomesResponse,
  PriceTarget,
  PriceView,
  ReferenceResponse,
  RevenueResponse,
  TargetsResponse,
} from "./types";

/** A structured API result: success carries `data`, failure carries the body. */
export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; body: unknown };

/** Body accepted by `PUT /api/crops/{crop}`. */
export interface CropInputsBody {
  costMode: "per_acre" | "whole_farm";
  costValue: number;
  expectedYield: number;
  plantedAcres?: number;
  source?: string;
}

/** Body accepted by `PUT /api/targets/{crop}/{type}`. */
export interface TargetBody {
  targetPrice?: number;
  profitAmount?: number;
}

/** HTTP methods the client issues. */
type Method = "GET" | "POST" | "PUT" | "DELETE";

/**
 * The API client. Construct once and share; set {@link onUnauthorized} to route
 * to Passcode entry when the session is rejected.
 */
export class ApiClient {
  /** Invoked when a protected request returns 401 (Req 1.6). */
  onUnauthorized: (() => void) | null = null;

  /**
   * Issue a request to `/api{path}` with the stored bearer token attached.
   * Returns a structured result; never throws for HTTP errors. A network
   * failure resolves to `{ ok: false, status: 0 }`.
   */
  private async request<T>(
    method: Method,
    path: string,
    body?: unknown,
    options?: { isLogin?: boolean }
  ): Promise<ApiResult<T>> {
    const headers: Record<string, string> = {};
    const session = loadSession();
    if (session !== null) {
      headers["authorization"] = `Bearer ${session.token}`;
    }
    if (body !== undefined) {
      headers["content-type"] = "application/json";
    }

    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl()}/api${path}`, {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      // Network-level failure — surfaced as status 0 so callers can render an
      // "unavailable" state without blocking (Req 9.7).
      return { ok: false, status: 0, body: { error: "network_error" } };
    }

    // A 401 on a protected route means the session is gone/expired: clear it
    // and route to Passcode entry (Req 1.6). Login's own 401 is a wrong
    // passcode and must be surfaced to that view instead.
    if (response.status === 401 && options?.isLogin !== true) {
      clearSession();
      if (this.onUnauthorized !== null) {
        this.onUnauthorized();
      }
    }

    const parsed = await this.parseBody(response);
    if (response.ok) {
      return { ok: true, status: response.status, data: parsed as T };
    }
    return { ok: false, status: response.status, body: parsed };
  }

  /** Parse a JSON body defensively; a 204/empty body yields `undefined`. */
  private async parseBody(response: Response): Promise<unknown> {
    if (response.status === 204) {
      return undefined;
    }
    const text = await response.text();
    if (text.length === 0) {
      return undefined;
    }
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  // --- Auth (Req 1) -------------------------------------------------------

  /**
   * Authenticate with the shared Passcode. On success persists the session and
   * returns it; a wrong passcode is `{ ok: false, status: 401 }` and a lockout
   * is `{ ok: false, status: 423 }` (Req 1.2, 1.3, 1.5).
   */
  async login(passcode: string): Promise<ApiResult<LoginSuccess>> {
    const result = await this.request<LoginSuccess>(
      "POST",
      "/auth/login",
      { passcode },
      { isLogin: true }
    );
    if (result.ok) {
      saveSession(result.data);
    }
    return result;
  }

  /** Terminate the current session server-side, then clear it locally (Req 1.7). */
  async logout(): Promise<void> {
    await this.request<void>("POST", "/auth/logout");
    clearSession();
  }

  /** Report the current authenticated state (Req 1.6). */
  async session(): Promise<ApiResult<{ authenticated: boolean; sessionId?: string }>> {
    return this.request("GET", "/session");
  }

  // --- Crops (Req 2, 10) --------------------------------------------------

  getCrop(crop: Crop): Promise<ApiResult<CropInputsResponse>> {
    return this.request("GET", `/crops/${crop}`);
  }

  putCrop(crop: Crop, body: CropInputsBody): Promise<ApiResult<CropInputsResponse>> {
    return this.request("PUT", `/crops/${crop}`, body);
  }

  // --- Breakeven (Req 3) --------------------------------------------------

  getBreakeven(crop: Crop): Promise<ApiResult<BreakevenResponse>> {
    return this.request("GET", `/breakeven/${crop}`);
  }

  // --- Price (Req 4, 9) ---------------------------------------------------

  getPrice(crop: Crop): Promise<ApiResult<PriceView>> {
    return this.request("GET", `/price/${crop}`);
  }

  submitOverride(crop: Crop, price: number): Promise<ApiResult<PriceView>> {
    return this.request("POST", `/price/${crop}/override`, { price });
  }

  getReference(crop: Crop): Promise<ApiResult<ReferenceResponse>> {
    return this.request("GET", `/price/${crop}/reference`);
  }

  getOutcomes(crop: Crop): Promise<ApiResult<OutcomesResponse>> {
    return this.request("GET", `/price/${crop}/outcomes`);
  }

  // --- Revenue (Req 5) ----------------------------------------------------

  getRevenue(crop: Crop): Promise<ApiResult<RevenueResponse>> {
    return this.request("GET", `/revenue/${crop}`);
  }

  // --- Targets (Req 6) ----------------------------------------------------

  getTargets(crop: Crop): Promise<ApiResult<TargetsResponse>> {
    return this.request("GET", `/targets/${crop}`);
  }

  putTarget(
    crop: Crop,
    type: TargetType,
    body: TargetBody
  ): Promise<ApiResult<PriceTarget>> {
    return this.request("PUT", `/targets/${crop}/${type}`, body);
  }

  deleteTarget(crop: Crop, type: TargetType): Promise<ApiResult<unknown>> {
    return this.request("DELETE", `/targets/${crop}/${type}`);
  }

  // --- Dashboard (Req 8) --------------------------------------------------

  getDashboard(): Promise<ApiResult<DashboardResponse>> {
    return this.request("GET", "/dashboard");
  }
}
